import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import PostGrid from './components/PostGrid';
import Sidebar from './components/Sidebar';
import AdSlot from './components/AdSlot';
import NewsletterForm from '@/components/feature/NewsletterForm';
import NicheBanner from './components/NicheBanner';
import { getPublishedPosts } from '@/utils/scheduler';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import NextPublishCountdown from '@/components/feature/NextPublishCountdown';

const BASE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://roameurope.blog';

const websiteSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'roameurope.blog',
  alternateName: 'Roam Europe Blog',
  url: BASE_URL,
  description: 'Expert relationship advice, dating tips, and love stories. Honest, research-backed writing about love, communication, self-love, and human connection. New articles daily.',
  inLanguage: 'en-US',
  publisher: {
    '@type': 'Organization',
    name: 'roameurope.blog',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: 'https://public.readdy.ai/ai/img_res/66883f2c29589234b2b41d3c1e883708.jpg',
      width: 180,
      height: 180,
    },
    sameAs: [
      'https://twitter.com/roameuropeblog',
      'https://www.instagram.com/roameuropeblog',
      'https://www.pinterest.com/roameuropeblog',
    ],
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${BASE_URL}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

export default function HomePage() {
  const publishedPosts = useMemo(() => getPublishedPosts(), []);
  const featuredPost = publishedPosts[0];
  const gridPosts = publishedPosts.slice(1);

  useSeoMeta({
    title: 'Relationship Advice, Dating Tips & Love Stories',
    description: 'Honest, thoughtful writing about love, relationships, dating, and the beautiful complexity of human connection. New articles published daily.',
    image: featuredPost?.image,
    url: '/',
    type: 'website',
  });

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: websiteSchema }}
      />
      <Navbar />
      <main className="bg-white dark:bg-stone-950 transition-colors duration-300">
        {/* Niche identity banner — clearly signals relationship advice content */}
        <NicheBanner />

        {/* Hero */}
        {featuredPost && (
          <div className="w-full">
            <HeroSection featuredPost={featuredPost} />
          </div>
        )}

        {/* Next publish banner */}
        <NextPublishCountdown variant="banner" />

        {/* Header ad */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-8">
          <AdSlot slot="header" />
        </div>

        {/* Main content + sidebar */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-10">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Posts grid */}
            <div className="flex-1 min-w-0">
              <PostGrid posts={gridPosts} title="Latest Articles" />
            </div>

            {/* Sidebar */}
            <div className="w-full lg:w-80 flex-shrink-0">
              <Sidebar recentPosts={publishedPosts} />
            </div>
          </div>
        </div>

        {/* Newsletter banner */}
        <div className="mt-16">
          <NewsletterForm variant="banner" />
        </div>
      </main>
      <Footer />
    </>
  );
}
